import React, { Component } from "react"
import { connect } from "react-redux"
import { compose } from "redux"
import { translate } from "react-i18next"
import PropTypes from "prop-types"
import Widget from "../layout/widget/Widget"
import DrilldownColumnChart from "../charts/generic/DrilldownColumnChart"
import DrilldownStackedChart from "../charts/generic/DrilldownStackedChart"
import SelectList from "../inputs/SelectList"
import {
  convertFilterStateToUrlParams,
  convertFilterStateToPostLocations,
} from "../../utils/filter-utils"
import * as performanceComparisonChartActions from "../../state/actions/performanceComparisonChartActions"
import FilterLoadingSpinner from "../../components/filters/FilterLoadingSpinner"

class PerformanceComparison extends Component {
  constructor(props) {
    super(props)

    this.locationTranslation = props.t("Employee.LocationFullName")
    this.generationTranslation = props.t("Employee.Generation")
    this.averageTranslation = props.t("Common.Average")
    this.countTranslation = props.t("Common.Count")
    this.percentageTranslation = props.t("Common.Percentage")
    this.yearsTranslation = props.t("Common.Years")

    const enabledOption = props.data.comparisonSelectOptions.find((o) => {
      return o.enabled === true
    })

    let typeSel = 0
    if (enabledOption !== undefined) {
      typeSel = enabledOption.id
    }

    const extraExportParameter = `&groupByLocation=${true}&scoreType=${typeSel}`
    var summaryScoreType = this.getSummaryScoreType(typeSel)
    this.state = {
      comparisonSelection: 0,
      typeSelection: typeSel,
      chartData: this.props.data.locationDrilldownSummary,
      xAxisLabel: this.locationTranslation,
      yAxisLabel: this.averageTranslation,
      summaryScoreType: summaryScoreType,
      chartDrilldownLevel: 0,
      extraExportParameter,
      isLoading: false,
      useStackedChart: this.useStackedChart(summaryScoreType)
    }
  }

  componentDidUpdate = (previousProps) => {
    if (previousProps.data !== this.props.data) {
      this.setChartData(
        this.props.data,
        this.state.comparisonSelection,
        this.state.summaryScoreType
      )
    }
  }

  onComparisonChange = (e) => {
    const comparison = e.target.value

    this.setState({
      // Set drill back to 0
      chartDrilldownLevel: 0
    })

    this.setChartData(this.props.data, comparison, this.state.summaryScoreType)
  }

  getSummaryScoreType = (typeSel) => {
    const found = this.props.data.comparisonSelectOptions.find((element) => {
      return element.id == typeSel
    })

    return found.summaryScoreType
  }

  onDrillDown = (event, chart) => {
    if (this.state.chartDrilldownLevel === 0) {
      this.setState(
        {
          chartDrilldownLevel: this.state.chartDrilldownLevel + 1,
        },
        () => {
          this.setChartTitle(chart, this.state.chartDrilldownLevel)
        }
      )
    } else {
      this.setChartTitle(chart, this.state.chartDrilldownLevel + 1)
      this.setState({ chartDrilldownLevel: this.state.chartDrilldownLevel + 1 })
    }
  }

  onDrillUp = (event, chart) => {
    // Look at level number to see if this is the top series
    if (event.seriesOptions._levelNumber === 0) {
      this.setState({
        // Set drill back to 0
        chartDrilldownLevel: 0,
      })
    } else {
      this.setChartTitle(chart, this.state.chartDrilldownLevel - 1)
      this.setState({ chartDrilldownLevel: this.state.chartDrilldownLevel - 1 })
    }
  }

  setChartTitle = (chart, drilldownLevel) => {
    chart.xAxis[0].setTitle({
      text: this.props.t(`LocationTierLabel_Lvl_${drilldownLevel - 1}`),
    })
  }

  getExtraExportParameters = (comparisonSelection, typeSelection) => {
    return `&groupByLocation=${comparisonSelection == 0}&scoreType=${typeSelection}`
  }

  onTypeChange = (e) => {
    const dataType = e.target.value

    this.props.changeComparisonType(
      dataType,
      convertFilterStateToUrlParams(this.props.filters),
      this.props.dashboard,
      convertFilterStateToPostLocations(this.props.filters)
    )

    const extraExportParameter = this.getExtraExportParameters(
      this.state.comparisonSelection,
      dataType
    )

    var summaryScoreType = this.getSummaryScoreType(dataType)

    this.setState({
      typeSelection: dataType,
      summaryScoreType: summaryScoreType,
      extraExportParameter,
      // Set drill back to 0
      chartDrilldownLevel: 0,
      isLoading: true
    })
  }

  setChartData = (data, comparisonSelection, summaryScoreType) => {
    let xAxisLabel = ""
    const yAxisLabel = this.getAxisText(summaryScoreType)
    let chartData

    if (comparisonSelection == 0) {
      xAxisLabel = this.locationTranslation
      chartData = data.locationDrilldownSummary
    } else {
      xAxisLabel = this.generationTranslation
      chartData = data.generationDrilldownSummary
    }

    const extraExportParameter = this.getExtraExportParameters(
      comparisonSelection,
      this.state.typeSelection
    )

    this.setState({
      chartData,
      comparisonSelection,
      xAxisLabel,
      yAxisLabel,
      extraExportParameter,
    })
  }

  getComparisonSelectOptions = () => {
    return [
      { id: 0, text: this.locationTranslation, yText: this.averageTranslation },
      { id: 1, text: this.generationTranslation, yText: this.averageTranslation },
    ]
  }

  getDataSelectOptions = (options) => {
    if (!options) return []

    const enabledOptions = options.find((o) => {
      return o.enabled === true
    })

    if (enabledOptions && enabledOptions[0]) {
      this.setState({
        typeSelection: enabledOptions[0].id,
      })
    }

    return options.map((opt) => {
      return { ...opt, text: this.props.t(opt.text) }
    })
  }

  chartUpdated = () => {
    this.setState({
      isLoading: false,
      useStackedChart: this.useStackedChart(this.state.summaryScoreType)
    })
  }

  formatXAxis = (value) => {
    if (isNaN(value)) {
      const i = value.lastIndexOf(">")
      let text = value
      if (i > -1) {
        text = value.substring(i + 1).trim()
      }
      return text
    }
    return value
  }

  getAxisText = (summaryScoreType) => {
    // Sum
    if (summaryScoreType === 1) return this.countTranslation
    // Percentage
    else if (summaryScoreType === 2 || summaryScoreType === 3) return this.percentageTranslation
    // Average
    return this.averageTranslation
  }

  formatDataLabel = (context) => {
    // Average
    if (this.state.summaryScoreType === 0) return context.y

    // Sum
    if (this.state.summaryScoreType === 1) return context.y

    // Percentage
    return `${context.y}%`
  }

  formatTooltip = (context) => {
    let text = `<b>${`${context.series.name} > ${context.key}`}:</b> ${context.y}`

    if (this.state.comparisonSelection === 1) {
      text = `<b>${context.series.name}:</b> ${context.y}`
    }

    if (this.state.summaryScoreType === 2) text += "%"

    if (this.state.typeSelection === 100) text += ` ${this.yearsTranslation}`

    return text
  }

  useStackedChart = (scoreType) => {
    // Potential selection to use a stack chart instead of regular column drilldown
    return scoreType === 3
  }

  getSampleSize = (sampleSize) => {
    if (this.state.comparisonSelection == 1) {
      return {
        total: sampleSize.total,
        sample: sampleSize.sample,
        percentage: sampleSize.percentage,
        showWarning: sampleSize.showWarning,
        hireDateWarning: sampleSize.hireDateWarning,
        invalidDobSample: sampleSize.invalidDobSample,
        dobWarning: sampleSize.dobWarning,
      }
    }

    return {
      total: sampleSize.total,
      sample: sampleSize.sample,
      percentage: sampleSize.percentage,
      showWarning: sampleSize.showWarning,
      hireDateWarning: sampleSize.hireDateWarning,
      invalidDobSample: 0,
      dobWarning: false,
    }
  }

  render() {
    const {
      appliedFilters, exportEndpoint, columns, data, subtitleKey, sampleSize,
    } = this.props
    const { typeSelection } = this.state

    let yAxisMaxValue
    switch (parseInt(typeSelection)) {
      case 0: // Performance Score
      case 5: // NPS/Engagement Score
      case 1: // Potential Score
        yAxisMaxValue = 100
        break
      case 3: // Happiness 1-10
        yAxisMaxValue = 10
        break
      default:
        yAxisMaxValue = undefined
        break
    }
    const titleKey = "Widgets.PerformanceComparison.Title"
    return (
      <Widget
        columns={columns}
        titleKey={titleKey}
        helpKey="Widgets.PerformanceComparison.Help"
        exportEndpoint={exportEndpoint}
        appliedFilters={appliedFilters}
        exportParams={this.state.extraExportParameter}
        subtitleKey={subtitleKey}
        sampleSize={this.getSampleSize(sampleSize)}
      >
        <div className="col-sm-12">
          <div className="row">
            <div className="col-md-2 col-sm-12 float-left-print-only" />
            <div className="col-md-4 col-sm-12 float-left-print-only">
              <div className="row">
                <div className="col-md-10 float-left-print-only">
                  <SelectList
                    onSelectChange={this.onTypeChange}
                    data={this.getDataSelectOptions(data.comparisonSelectOptions)}
                  />
                </div>
                <div
                  className="col-md-2 float-left-print-only"
                  style={{
                    paddingLeft: "0",
                    paddingRight: "0",
                    textAlign: "center",
                    lineHeight: "38px",
                  }}
                >
                  vs
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-12 float-left-print-only">
              <div className="row">
                <div className="col-md-10">
                  <SelectList
                    onSelectChange={this.onComparisonChange}
                    data={this.getComparisonSelectOptions()}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              {this.state.isLoading && <FilterLoadingSpinner />}
              {!this.state.useStackedChart && (
                <DrilldownColumnChart
                  data={this.state.chartData}
                  yAxisTitle={this.state.yAxisLabel}
                  yAxisMaxValue={yAxisMaxValue}
                  xAxisTitle={this.state.xAxisLabel}
                  allowUpdate
                  scrollbarEnabled
                  xAxisFormatter={this.formatXAxis}
                  tooltipFormatter={this.formatTooltip}
                  dataLabelFormatter={this.formatDataLabel}
                  onDrillUp={this.onDrillUp}
                  onDrillDown={this.onDrillDown}
                  chartDrilldownLevel={this.state.chartDrilldownLevel}
                  isPerformanceComparison
                  titleKey={titleKey}
                  subtitleKey={subtitleKey}
                  onComponentUpdate={this.chartUpdated}
                />
              )}
              {this.state.useStackedChart && (
                <DrilldownStackedChart
                  data={this.state.chartData}
                  yAxisTitle={this.percentageTranslation}
                  yAxisMaxValue={yAxisMaxValue}
                  xAxisTitle={this.state.xAxisLabel}
                  allowUpdate
                  scrollbarEnabled
                  xAxisFormatter={this.formatXAxis}
                  onDrillUp={this.onDrillUp}
                  onDrillDown={this.onDrillDown}
                  chartDrilldownLevel={this.state.chartDrilldownLevel}
                  isPerformanceComparison
                  titleKey={titleKey}
                  subtitleKey={subtitleKey}
                  onComponentUpdate={this.chartUpdated}
                />
              )}
            </div>
          </div>
        </div>
      </Widget>
    )
  }
}

PerformanceComparison.propTypes = {
  dashboard: PropTypes.string,
  appliedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  columns: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
  exportEndpoint: PropTypes.string.isRequired,
  subtitleKey: PropTypes.string,
  sampleSize: PropTypes.object,
}

function mapStateToProps(state) {
  return {
    filters: state.filters,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    changeComparisonType: (scoreType, filtersAsParams, dashboard, filterLocations) => {
      dispatch(performanceComparisonChartActions.changeComparisonType(
        dashboard,
        scoreType,
        filtersAsParams,
        filterLocations
      ))
    },
  }
}

export default compose(translate(), connect(mapStateToProps, mapDispatchToProps))(PerformanceComparison)



// WEBPACK FOOTER //
// ./src/components/widgets/PerformanceComparison.jsx